import { NotificationVariants, UpdateActionEnum, request, type ApiPermissionFilter } from '@talos/kyoko';

type PermissionFilter = ApiPermissionFilter & { FilterID: string };

interface SaveMarketAccountPermissionFiltersArgs<T extends PermissionFilter> {
  permissions: T[];
  deleteUrl: (permission: T) => string;
  createUrl: string;
  createBody: (permission: T) => object;
  marketAccountName: (permission: T) => string;
  addToast: (toast: { text: string; variant: typeof NotificationVariants.Negative }) => void;
}

/** Creates and deletes subaccount-to-market-account permissions one row at a time. */
export async function saveMarketAccountPermissionFilters<T extends PermissionFilter>({
  permissions,
  deleteUrl,
  createUrl,
  createBody,
  marketAccountName,
  addToast,
}: SaveMarketAccountPermissionFiltersArgs<T>): Promise<{ applied: T[]; failedFilterIDs: string[] }> {
  const deletions = permissions.filter(permission => permission.UpdateAction === UpdateActionEnum.Remove);
  const creates = permissions.filter(permission => permission.UpdateAction === UpdateActionEnum.Initial);

  const attemptedDeletions = await Promise.all(
    deletions.map(permission =>
      request('DELETE', deleteUrl(permission))
        .then(() => ({ status: 'fulfilled' as const, data: permission }))
        .catch(() => ({ status: 'rejected' as const, data: permission }))
    )
  );

  const successfulDeletions: T[] = attemptedDeletions
    .filter(result => result.status === 'fulfilled')
    .map(result => result.data);
  const failedDeletions: T[] = attemptedDeletions
    .filter(result => result.status === 'rejected')
    .map(result => result.data);

  const attemptedCreates = await Promise.all(
    creates.map(permission =>
      request('POST', createUrl, createBody(permission))
        .then((res: { data: T[] }) => ({
          status: 'fulfilled' as const,
          data: res.data[0],
        }))
        .catch(() => ({ status: 'rejected' as const, data: permission }))
    )
  );

  const successfulCreates: T[] = attemptedCreates
    .filter(result => result.status === 'fulfilled')
    .map(result => result.data);
  const failedCreates: T[] = attemptedCreates.filter(result => result.status === 'rejected').map(result => result.data);

  for (const failedDeletion of failedDeletions) {
    addToast({
      text: `Failed to delete permission for Subaccount: "${failedDeletion.Subject.SubAccount}" to Market Account: "${marketAccountName(failedDeletion)}"`,
      variant: NotificationVariants.Negative,
    });
  }

  for (const failedCreate of failedCreates) {
    addToast({
      text: `Failed to update permission for Subaccount: "${failedCreate.Subject.SubAccount}" to Market Account: "${marketAccountName(failedCreate)}"`,
      variant: NotificationVariants.Negative,
    });
  }

  return {
    applied: successfulDeletions.concat(successfulCreates),
    failedFilterIDs: failedDeletions
      .map(failed => failed.FilterID)
      .concat(failedCreates.map(failed => failed.FilterID)),
  };
}
